/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * QubePro çalışma alanı odak modunun tek sahipli, erişilebilir ve yeniden
 * kullanılabilir davranış sözleşmesi. Ürün sayfaları Layout veya DOM kabuğuna
 * yerel fullscreen sınıfları eklemek yerine bu hook/component çiftini kullanır.
 */

import type {
    ButtonHTMLAttributes,
    Dispatch,
    ReactNode,
    RefObject,
    SetStateAction,
} from 'react';
import {
    useCallback,
    useEffect,
    useId,
    useRef,
    useState,
} from 'react';
import {
    getNextWorkspaceFocusLevel,
    getPreviousWorkspaceFocusLevel,
    normalizeWorkspaceFocusShortcutKey,
    type WorkspaceFocusLevel,
} from './WorkspaceFocusMode.state';
import './WorkspaceFocusMode.css';

export type { WorkspaceFocusLevel } from './WorkspaceFocusMode.state';

const WORKSPACE_FOCUS_OWNER_EVENT = 'qubepro:workspace-focus-owner';
const workspaceFocusOwners = new Set<string>();
let preferredWorkspaceFocusOwnerId: string | null = null;

interface WorkspaceFocusOwnerEventDetail {
    ownerId: string;
}

export interface UseWorkspaceFocusModeOptions<T extends HTMLElement> {
    targetRef: RefObject<T | null>;
    /** Opt out when the surrounding surface already owns focus/fullscreen. */
    enabled?: boolean;
    companionRef?: RefObject<HTMLElement | null>;
    shortcutKey?: string;
    pageSelector?: string;
    mainSelector?: string;
    layoutSelector?: string;
    onLayoutChange?: () => void;
}

export interface WorkspaceFocusModeController {
    level: WorkspaceFocusLevel;
    isActive: boolean;
    isFullscreen: boolean;
    setLevel: (level: WorkspaceFocusLevel) => void;
    setActive: (active: boolean) => void;
    advance: () => void;
    stepBack: () => void;
    toggle: () => void;
    markPreferred: () => void;
}

export interface WorkspaceViewLevelController {
    level: WorkspaceFocusLevel;
    isActive: boolean;
    isFullscreen: boolean;
    setLevel: Dispatch<SetStateAction<WorkspaceFocusLevel>>;
    advance: () => void;
    stepBack: () => void;
    reset: () => void;
}

export interface UseWorkspaceFullscreenLayoutOptions<T extends HTMLElement> {
    active: boolean;
    targetRef: RefObject<T | null>;
    layoutSelector?: string;
    onLayoutChange?: () => void;
    onUnavailable?: () => void;
}

export interface WorkspaceFocusModeToggleProps
    extends Omit<
        ButtonHTMLAttributes<HTMLButtonElement>,
        'aria-label' | 'aria-pressed' | 'aria-keyshortcuts' | 'onClick' | 'title'
    > {
    mode?: WorkspaceFocusLevel;
    active?: boolean;
    enterLabel: string;
    fullscreenLabel?: string;
    exitLabel: string;
    onToggle: () => void;
    children?: ReactNode;
}

export function isWorkspaceFocusEditableTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;

    return Boolean(
        target.closest(
            'input, textarea, select, [contenteditable="true"], [contenteditable=""], [role="textbox"], [data-workspace-focus-shortcut-disabled="true"]',
        ),
    );
}

/**
 * DataTable ve Modal'ın paylaştığı tek üç-kademe state makinesi.
 * DOM/Layout etkileri bu controller'ın tüketicisine aittir; kademe sırası,
 * ileri/geri geçiş ve reset burada tek kez tanımlanır.
 */
export function useWorkspaceViewLevel(
    onLevelChange?: (level: WorkspaceFocusLevel) => void,
): WorkspaceViewLevelController {
    const [level, setLevelState] = useState<WorkspaceFocusLevel>('normal');
    const onLevelChangeRef = useRef(onLevelChange);

    useEffect(() => {
        onLevelChangeRef.current = onLevelChange;
    }, [onLevelChange]);

    const setLevel: Dispatch<SetStateAction<WorkspaceFocusLevel>> = useCallback((nextValue) => {
        setLevelState((current) => {
            const next = typeof nextValue === 'function' ? nextValue(current) : nextValue;
            if (next !== current) onLevelChangeRef.current?.(next);
            return next;
        });
    }, []);

    const advance = useCallback(() => {
        setLevel((current) => getNextWorkspaceFocusLevel(current));
    }, [setLevel]);

    const stepBack = useCallback(() => {
        setLevel((current) => getPreviousWorkspaceFocusLevel(current));
    }, [setLevel]);

    const reset = useCallback(() => {
        setLevel('normal');
    }, [setLevel]);

    return {
        level,
        isActive: level !== 'normal',
        isFullscreen: level === 'fullscreen',
        setLevel,
        advance,
        stepBack,
        reset,
    };
}

/** DataTable ve Modal için tek Layout tam-ekran efekti. */
export function useWorkspaceFullscreenLayout<T extends HTMLElement>({
    active,
    targetRef,
    layoutSelector = '.layout',
    onLayoutChange,
    onUnavailable,
}: UseWorkspaceFullscreenLayoutOptions<T>): void {
    useEffect(() => {
        if (!active) return undefined;

        const layout = targetRef.current?.closest(layoutSelector);
        if (!(layout instanceof HTMLElement)) {
            onUnavailable?.();
            return undefined;
        }

        layout.classList.add('workspace-focus-layout-active');
        const animationFrame = window.requestAnimationFrame(() => {
            onLayoutChange?.();
        });

        return () => {
            window.cancelAnimationFrame(animationFrame);
            layout.classList.remove('workspace-focus-layout-active');
            window.requestAnimationFrame(() => {
                onLayoutChange?.();
            });
        };
    }, [active, layoutSelector, onLayoutChange, onUnavailable, targetRef]);
}

function announceWorkspaceFocusOwner(ownerId: string) {
    preferredWorkspaceFocusOwnerId = ownerId;
    document.dispatchEvent(
        new CustomEvent<WorkspaceFocusOwnerEventDetail>(WORKSPACE_FOCUS_OWNER_EVENT, {
            detail: { ownerId },
        }),
    );
}

export function useWorkspaceFocusMode<T extends HTMLElement>({
    targetRef,
    enabled = true,
    companionRef,
    shortcutKey = 'f',
    pageSelector = '.page-container, .qp-system-page',
    mainSelector = '.main-content, .qp-system-shell__main',
    layoutSelector = '.layout, .qp-system-shell',
    onLayoutChange,
}: UseWorkspaceFocusModeOptions<T>): WorkspaceFocusModeController {
    const reactOwnerId = useId();
    const ownerId = `workspace-focus-${reactOwnerId}`;
    const normalizedShortcutKey = normalizeWorkspaceFocusShortcutKey(shortcutKey);
    const onLevelChange = useCallback((next: WorkspaceFocusLevel) => {
        if (enabled && next !== 'normal') announceWorkspaceFocusOwner(ownerId);
    }, [enabled, ownerId]);
    const viewLevel = useWorkspaceViewLevel(onLevelChange);
    const level = enabled ? viewLevel.level : 'normal';
    const isActive = enabled && viewLevel.isActive;
    const isFullscreen = enabled && viewLevel.isFullscreen;

    useEffect(() => {
        if (!enabled) viewLevel.reset();
    }, [enabled, viewLevel.reset]);

    const markPreferred = useCallback(() => {
        if (!enabled) return;
        preferredWorkspaceFocusOwnerId = ownerId;
    }, [enabled, ownerId]);

    const setLevel = useCallback((nextLevel: WorkspaceFocusLevel) => {
        if (!enabled) return;
        viewLevel.setLevel(nextLevel);
    }, [enabled, viewLevel.setLevel]);

    const setActive = useCallback((active: boolean) => {
        setLevel(active ? 'fullscreen' : 'normal');
    }, [setLevel]);

    const advance = useCallback(() => {
        if (!enabled) return;
        viewLevel.advance();
    }, [enabled, viewLevel.advance]);

    const stepBack = useCallback(() => {
        if (!enabled) return;
        viewLevel.stepBack();
    }, [enabled, viewLevel.stepBack]);

    const toggle = useCallback(() => {
        if (!enabled) return;
        viewLevel.setLevel((current) => current === 'normal' ? 'fullscreen' : 'normal');
    }, [enabled, viewLevel.setLevel]);
    const handleFullscreenUnavailable = useCallback(() => {
        if (!enabled) return;
        viewLevel.setLevel('focus');
    }, [enabled, viewLevel.setLevel]);

    useEffect(() => {
        if (!enabled || !normalizedShortcutKey) return undefined;
        workspaceFocusOwners.add(ownerId);
        if (!preferredWorkspaceFocusOwnerId) {
            preferredWorkspaceFocusOwnerId = ownerId;
        }

        return () => {
            workspaceFocusOwners.delete(ownerId);
            if (preferredWorkspaceFocusOwnerId === ownerId) {
                preferredWorkspaceFocusOwnerId =
                    workspaceFocusOwners.values().next().value ?? null;
            }
        };
    }, [enabled, ownerId, normalizedShortcutKey]);

    useEffect(() => {
        if (!enabled) return undefined;
        const handleOwnerChange = (event: Event) => {
            const ownerEvent = event as CustomEvent<WorkspaceFocusOwnerEventDetail>;
            if (ownerEvent.detail?.ownerId !== ownerId) {
                viewLevel.reset();
            }
        };

        document.addEventListener(WORKSPACE_FOCUS_OWNER_EVENT, handleOwnerChange);
        return () => {
            document.removeEventListener(WORKSPACE_FOCUS_OWNER_EVENT, handleOwnerChange);
        };
    }, [enabled, ownerId, viewLevel.reset]);

    useEffect(() => {
        if (!enabled) return undefined;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (
                event.defaultPrevented ||
                event.isComposing ||
                event.altKey ||
                event.ctrlKey ||
                event.metaKey
            ) {
                return;
            }

            // A dialog owns Escape and letter shortcuts while it has focus.
            // Do not change the workspace behind it before Modal handles the key.
            if (event.target instanceof Element && event.target.closest('[role="dialog"], [role="alertdialog"]')) return;

            if (event.key === 'Escape' && isActive) {
                event.preventDefault();
                stepBack();
                return;
            }

            const normalizedEventKey = normalizeWorkspaceFocusShortcutKey(event.key);
            if (
                !normalizedEventKey ||
                !normalizedShortcutKey ||
                normalizedEventKey !== normalizedShortcutKey ||
                preferredWorkspaceFocusOwnerId !== ownerId ||
                isWorkspaceFocusEditableTarget(event.target)
            ) {
                return;
            }

            event.preventDefault();
            advance();
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [advance, enabled, isActive, normalizedShortcutKey, ownerId, stepBack]);

    useEffect(() => {
        if (!enabled || !isActive) return undefined;

        const target = targetRef.current;
        const pageContainer = target?.closest(pageSelector);
        const mainContent = target?.closest(mainSelector);
        if (!(target instanceof HTMLElement) || !(pageContainer instanceof HTMLElement)) {
            viewLevel.reset();
            return undefined;
        }

        const focusPathElements: HTMLElement[] = [];
        let focusPathElement = target.parentElement;
        while (focusPathElement && focusPathElement !== pageContainer) {
            focusPathElements.push(focusPathElement);
            focusPathElement = focusPathElement.parentElement;
        }
        const focusHost = focusPathElements[focusPathElements.length - 1] ?? target;
        const previousScrollTop =
            mainContent instanceof HTMLElement ? mainContent.scrollTop : 0;

        target.classList.add('workspace-focus-active');
        const companion = companionRef?.current;
        companion?.classList.add('workspace-focus-companion');
        focusHost.classList.add('workspace-focus-host');
        focusPathElements.forEach((element) => {
            element.classList.add('workspace-focus-path');
        });
        pageContainer.classList.add('workspace-focus-page-active');
        if (mainContent instanceof HTMLElement) {
            mainContent.classList.add('workspace-focus-main-active');
            mainContent.scrollTop = 0;
        }

        const animationFrame = window.requestAnimationFrame(() => {
            onLayoutChange?.();
        });

        return () => {
            window.cancelAnimationFrame(animationFrame);
            target.classList.remove('workspace-focus-active');
            companion?.classList.remove('workspace-focus-companion');
            focusHost.classList.remove('workspace-focus-host');
            focusPathElements.forEach((element) => {
                element.classList.remove('workspace-focus-path');
            });
            pageContainer.classList.remove('workspace-focus-page-active');
            if (mainContent instanceof HTMLElement) {
                mainContent.classList.remove('workspace-focus-main-active');
                window.requestAnimationFrame(() => {
                    mainContent.scrollTop = previousScrollTop;
                    onLayoutChange?.();
                });
            }
        };
    }, [
        enabled,
        isActive,
        companionRef,
        mainSelector,
        onLayoutChange,
        pageSelector,
        targetRef,
        viewLevel.reset,
    ]);

    useWorkspaceFullscreenLayout({
        active: isFullscreen,
        targetRef,
        layoutSelector,
        onLayoutChange,
        onUnavailable: handleFullscreenUnavailable,
    });

    return {
        level,
        isActive,
        isFullscreen,
        setLevel,
        setActive,
        advance,
        stepBack,
        toggle,
        markPreferred,
    };
}

export function WorkspaceFocusModeToggle({
    mode,
    active,
    enterLabel,
    fullscreenLabel,
    exitLabel,
    onToggle,
    children,
    type = 'button',
    ...buttonProps
}: WorkspaceFocusModeToggleProps) {
    const resolvedMode = mode ?? (active ? 'fullscreen' : 'normal');
    const isPressed = resolvedMode !== 'normal';
    const label =
        resolvedMode === 'normal'
            ? enterLabel
            : resolvedMode === 'focus'
                ? fullscreenLabel ?? exitLabel
                : exitLabel;

    return (
        <button
            {...buttonProps}
            type={type}
            onClick={onToggle}
            aria-pressed={isPressed}
            aria-keyshortcuts="F Escape"
            aria-label={label}
            title={label}
            data-workspace-focus-toggle="true"
            data-workspace-focus-level={resolvedMode}
        >
            {children}
        </button>
    );
}
